import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import {
  buildCourseDetailsSessionsRoute,
  buildScheduleSessionManageRoute
} from "../../../../routes/AdminRoutes";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import {
  courseLocationTypes,
  trainingObjectStatusIds
} from "../../../../constants/index";
import {
  fetchCourses,
  fetchCustomerIltDetails,
  addCourseSession
} from "../../../../redux/actions/contentCoursesActions";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { push } from "connected-react-router";
import { getDurationString } from "../../../../utils/datetime.js";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { buildCourseSessionDetailsRoute } from "../../../../routes/AdminRoutes";
import { Box, Button, Divider, Grid } from "@mui/material";
import IltSessionForm from "../../../../components/Forms/IltSessionForm";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import { formatDate } from "../../../../utils/datetime";
import usePermissions from "../../../../redux/selectors/usePermissions";
import AddInstructorButton from "../ManageInstructorsLocations/AddInstructorButton";
import AddTrainingLocationButton from "../ManageInstructorsLocations/AddTrainingLocationButton";

const selector = makeGetEntities();

const ScheduleSession = ({
  match: {
    params: { courseId }
  }
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { canManageInstructors } = usePermissions();
  const [formValues, setFormValues] = useState(null);

  const {
    courseData,
    customerIltDetails,
    trainingLocations,
    loading,
    error
  } = useSelector(({ contentCoursesState }) => ({
    courseData: selector(contentCoursesState, {
      type: "trainingObject",
      id: courseId
    }),
    customerIltDetails: selector(contentCoursesState, {
      type: "customerIltDetails"
    })[0],
    trainingLocations: selector(contentCoursesState, {
      type: "trainingLocation"
    }),
    loading: contentCoursesState.loading,
    error: contentCoursesState.error
  }));

  useEffect(() => {
    if (!courseData) {
      dispatch(fetchCourses(courseId));
    }
  }, [courseData]);

  const handleFetchIltDetails = () => {
    dispatch(fetchCustomerIltDetails());
  };

  useEffect(() => {
    handleFetchIltDetails();
  }, []);

  const valuesToFormValues = values => {
    const formValues = {};
    Object.keys(values).forEach(
      key =>
        (formValues[key] = key === "instructor" ? values[key].id : values[key])
    );
    return formValues;
  };

  const handleGoBack = () => {
    dispatch(push(buildCourseDetailsSessionsRoute(courseId)));
  };

  const handleSubmit = (values, onError) => {
    const valuesCopy = { ...values };
    valuesCopy.startDate = formatDate(values.startDate, "YYYY-MM-DD");
    valuesCopy.endDate = formatDate(values.endDate, "YYYY-MM-DD");
    valuesCopy.startTime = formatDate(values.startTime, "h:mm A");
    valuesCopy.endTime = formatDate(values.endTime, "h:mm A");
    valuesCopy.statusTypeId = parseInt(values.statusTypeId);
    dispatch(
      addCourseSession(
        courseId,
        valuesCopy,
        newVersionData => {
          dispatch(
            push(
              buildCourseSessionDetailsRoute(
                courseId,
                Object.keys(newVersionData)[0]
              )
            )
          );
        },
        error =>
          handleResponseError(
            error,
            onError,
            valuesCopy.virtualClassroomId,
            valuesCopy.instructor.text
          )
      )
    );
  };

  const handleResponseError = (
    error,
    errorCallback,
    virtualClassroomId,
    instructor
  ) => {
    const messages = {};
    const vcRegExp = /^Virtual [cC]lassroom.*is unavailable during the date\/time selected/;
    if (error.errors) {
      error.errors.forEach(errorMsgObject => {
        if (parseInt(errorMsgObject.status) === 422) {
          if (errorMsgObject.detail.match(vcRegExp)) {
            const virtualClassroom = customerIltDetails.virtualClassrooms.find(
              vc => vc.id === virtualClassroomId
            );
            if (virtualClassroom) {
              const sessionName = errorMsgObject.detail.substring(
                errorMsgObject.detail.indexOf('The ILT "') + 'The ILT "'.length,
                errorMsgObject.detail.indexOf('" was previously scheduled')
              );
              messages.vc = t("scheduleSession.error.virtualClassroom", {
                virtualClassroomName: virtualClassroom.name,
                iltName: sessionName
              });
            }
          } else {
            const iltInstructorRegExp = /^Instructor.* was previously scheduled for this instructor at this date and time/;
            const iltLocationRegExp = /^Location.* was previously scheduled for this location at this date and time/;
            if (errorMsgObject.detail.match(iltInstructorRegExp)) {
              const sessionName = errorMsgObject.detail.substring(
                errorMsgObject.detail.indexOf("Name:") + "Name:".length + 1,
                errorMsgObject.detail.length
              );
              messages.generic = t("scheduleSession.error.instructor", {
                instructor: instructor,
                iltName: sessionName
              });
            } else if (errorMsgObject.detail.match(iltLocationRegExp)) {
              const scheduledTime = errorMsgObject.detail.substring(
                errorMsgObject.detail.indexOf("Time:") + "Time:".length + 1,
                errorMsgObject.detail.length
              );
              const sessionName = errorMsgObject.detail.substring(
                errorMsgObject.detail.indexOf("Name:") + "Name:".length + 1,
                errorMsgObject.detail.indexOf("Time:") - 1
              );
              messages.generic = t("scheduleSession.error.location", {
                iltName: sessionName,
                scheduledTime: scheduledTime
              });
            } else {
              messages.generic = t("scheduleSession.error.generic");
            }
          }
        }
      });
    }
    Object.values(messages).forEach(message =>
      dispatch(enqueueErrorSnackbar({ message: message }))
    );

    if (errorCallback && typeof errorCallback === "function") {
      errorCallback();
    }
  };

  if (loading) {
    return (
      <Box display="flex" flex="1" alignItems="center" justifyContent="center">
        <LoadingSpinner />
      </Box>
    );
  } else if (!!customerIltDetails && !!courseData) {
    const {
      defaultIltLocation,
      virtualClassrooms,
      instructors,
      virtualClassroomAllowed
    } = customerIltDetails;
    const initialValues = {
      statusTypeId: trainingObjectStatusIds.active,
      displayInLibrary: "true",
      hideFromLibraryAfter: "end",
      locationType: defaultIltLocation
        ? defaultIltLocation
        : courseLocationTypes.online,
      seats: "",
      locationId: "",
      virtualClassroomId: "",
      instructor: "",
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      timeZone: ""
    };

    return (
      <AdminPageLayout
        title={
          <Breadcrumb
            routes={[
              t("manageCourses.coursesPageAdminTitle"),
              courseData ? courseData.name : "",
              t("scheduleSession.pageTitle")
            ]}
          />
        }
        headerActions={
          canManageInstructors && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                dispatch(push(buildScheduleSessionManageRoute(courseId)))
              }
              data-qa="add-course-btn"
            >
              {t("manageCourses.ManangeInstrAndLocButton")}
            </Button>
          )
        }
        onGoBackClick={handleGoBack}
      >
        <IltSessionForm
          initialValues={formValues ? formValues : initialValues}
          trainingHours={getDurationString(courseData.trainingHours, t)}
          duration={courseData.trainingHours}
          iltName={courseData.name}
          description={courseData.description}
          locations={trainingLocations}
          virtualClassrooms={virtualClassrooms}
          instructors={instructors}
          virtualClassroomAllowed={virtualClassroomAllowed}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            handleSubmit(values, () =>
              setFormValues(valuesToFormValues(values))
            );
          }}
          render={({ formFields, formikProps: { values, submitForm } }) => (
            <>
              <Box mt={4}>
                <Grid container spacing={3} sx={{ mt: 4 }}>
                  <Grid item xs={12} lg={6}>
                    <Box mb={4}>{formFields.name}</Box>
                    <Box mb={3}>{formFields.description}</Box>
                    <Box mb={4}>{formFields.trainingHours}</Box>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Box mb={4}>
                      <Box mb={1}>{formFields.isActive}</Box>
                      <Box mb={1}>{formFields.displayInLibrary}</Box>
                      {formFields.hideFromLibraryAfter}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Divider />
              <Box my={4}>
                <Grid container spacing={3} sx={{ mt: 4, mb: 5 }}>
                  <Grid container spacing={3} item xs={12}>
                    <Grid item xs={12} md={4} sx={{ flexGrow: 1 }}>
                      {formFields.locationType}
                    </Grid>
                    {values.locationType ===
                      courseLocationTypes.virtualClassroom && (
                      <Grid item xs={12} md sx={{ minWidth: 200 }}>
                        {formFields.virtualClassroom}
                      </Grid>
                    )}
                    {values.locationType === courseLocationTypes.inPerson && (
                      <Grid item xs={12} md sx={{ minWidth: 200 }}>
                        {formFields.location}
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 120 }}>
                      {formFields.capacity}
                    </Grid>
                    {canManageInstructors &&
                      values.locationType === courseLocationTypes.inPerson && (
                        <Grid item xs="auto" sx={{ alignItems: "center" }}>
                          <Box pt={1.5} width={154}>
                            <AddTrainingLocationButton
                              onSave={handleFetchIltDetails}
                              buttonProps={{ variant: "text" }}
                            />
                          </Box>
                        </Grid>
                      )}
                  </Grid>
                  <Grid container item xs={12} spacing={3}>
                    <Grid item sm={12} md>
                      {formFields.instructor}
                    </Grid>
                    {canManageInstructors && (
                      <Grid item xs="auto" sx={{ alignItems: "center" }}>
                        <Box pt={1.5} width={154}>
                          <AddInstructorButton
                            onSave={handleFetchIltDetails}
                            buttonProps={{ variant: "text" }}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 170 }}>
                    {formFields.startDate}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 170 }}>
                    {formFields.startTime}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 220 }}>
                    {formFields.timeZone}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 170 }}>
                    {formFields.endDate}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 170 }}>
                    {formFields.endTime}
                  </Grid>
                </Grid>
              </Box>
              <ButtonGroup align="right">
                <Button color="primary" onClick={handleGoBack}>
                  {t("scheduleSession.cancelBtn")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={submitForm}
                >
                  {t("scheduleSession.scheduleClassBtn")}
                </Button>
              </ButtonGroup>
            </>
          )}
        />
      </AdminPageLayout>
    );
  } else if (error) {
    // TODO: check for error & return error view
    return null;
  }
  return null;
};

export default ScheduleSession;

ScheduleSession.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The ID for the course
       */
      courseId: PropTypes.string
    })
  })
};
